import type { UseComboboxStateChange } from 'downshift';
import type { CSSProperties, ReactNode } from 'react';
import type { Security } from '../../types';

import type { BoxProps } from '../Core';
import type { FormControlSizes } from '../Form';
import type { ExposedAutocompleteDropdownProps } from '../Form/AutocompleteDropdown/types';

export const SymbolSelectorAppearance = {
  Basic: 'Basic',
  Ghost: 'Ghost',
  Search: 'Search',
  CurrencyPair: 'CurrencyPair',
} as const;
export type SymbolSelectorAppearance = (typeof SymbolSelectorAppearance)[keyof typeof SymbolSelectorAppearance];

export type SymbolSelectorProps = {
  className?: string;
  startOpen?: boolean;
  symbol?: string;
  /** Pass in your own list of Security[] to override the default one */
  availableSecurities?: Security[];
  /** Overrides default product-type list grouping when showGroups is true. */
  getGroup?: (security: Security) => string;
  /** Tab group when it differs from getGroup. */
  getTabGroup?: (security: Security) => string | undefined;
  /**
   * If passing this prop, you may need to do some cleanup of your state
   * for your input elements. The default behavior inside of useSearchSelect
   * clears the input value and focuses the element itself on open, and sets
   * it to the selected item's appropriate label on selection.
   */
  onIsOpenChange?: ((changes: UseComboboxStateChange<Security>) => void) | undefined;
  onSymbolChanged: (symbol?: string) => void;
  disabled?: boolean;
  invalid?: boolean;
  touched?: boolean;
  appearance: SymbolSelectorAppearance;
  size?: FormControlSizes;
  showClear?: boolean;
  showGroups?: boolean;
  dropdownId?: string;
  closeOnInputBlur?: boolean;
  /** For the Basic select appearance, whether or not to center the select header contents */
  centerBasicSelect?: boolean;
  showFavorites?: boolean;
  showSelectedSymbolDescription?: boolean;
  securityRendererMaxWidth?: string;
  readOnly?: boolean;
  placeholder?: string;
  closeMenuRef?: React.MutableRefObject<(() => void) | null>;
  renderTrigger?: (props: {
    openMenu: () => void;
    isOpen: boolean;
    disabled?: boolean;
    security?: Security;
  }) => ReactNode;
  isItemDisabled?: (security: Security) => boolean;
  /** Styles merged into the dropdown menu wrapper. */
  menuStyles?: CSSProperties;
} & ExposedAutocompleteDropdownProps &
  BoxProps;
